import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Zap, Package, Share2 } from 'lucide-react';

const features = [
  {
    label: 'Instant QR Generation',
    description: 'Create professional QR codes in seconds',
    icon: <Zap />,
    color: '#7c3aed', // Purple
  },
  {
    label: 'Multiple Formats',
    description: 'Support for URLs, text, WiFi, and more',
    icon: <Package />,
    color: '#ec4899', // Pink
  },
  {
    label: 'Download & Share',
    description: 'Export as PNG, SVG, or PDF formats',
    icon: <Share2 />,
    color: '#8b5cf6', // Purple variant
  },
];

const StatsSection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 10%;
    left: 5%;
    width: 200px;
    height: 200px;
    background: rgba(168, 85, 247, 0.1);
    border-radius: 50%;
    filter: blur(60px);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 10%;
    right: 5%;
    width: 250px;
    height: 250px;
    background: rgba(236, 72, 153, 0.08);
    border-radius: 50%;
    filter: blur(60px);
  }

  @media (min-width: 768px) {
    padding: 8rem 0;
  }
`;

const StatsContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 10;

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;

  @media (min-width: 768px) {
    margin-bottom: 6rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  letter-spacing: -0.025em;
  color: #1f2937;

  span {
    background: linear-gradient(45deg, #7c3aed, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (min-width: 640px) {
    font-size: 3rem;
  }

  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (min-width: 640px) {
    font-size: 1.375rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
  }

  @media (min-width: 1024px) {
    gap: 4rem;
  }
`;

const StatCard = styled.div`
  padding: 3rem 2rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(15px);
  border-radius: 2rem;
  position: relative;
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.08),
    inset 0 1px 1px rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.6);
  transition: all 0.4s ease;
  overflow: hidden;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible }) =>
    isVisible ? 'translateY(0)' : 'translateY(30px)'};
  transition-delay: ${({ delay }) => delay || '0ms'};

  &:hover {
    transform: translateY(-5px);
    box-shadow:
      0 20px 40px rgba(0, 0, 0, 0.12),
      inset 0 1px 1px rgba(255, 255, 255, 0.8);
  }

  @media (min-width: 768px) {
    padding: 3.5rem 2.5rem;
  }
`;

const LargeBackgroundIcon = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ color }) => color || 'rgba(124, 58, 237, 0.05)'};
  z-index: 1;
  pointer-events: none;

  svg {
    width: 120px;
    height: 120px;
    stroke-width: 1;
    opacity: 0.1;
  }
`;

const StatLabel = styled.h4`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  letter-spacing: -0.025em;
  color: #1f2937;
  position: relative;
  z-index: 10;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const StatDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
  font-size: 1rem;
  max-width: 90%;
  margin: 0 auto;
  position: relative;
  z-index: 10;

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
`;

export default function Stats() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <StatsSection ref={sectionRef}>
      <StatsContainer>
        <SectionHeader>
          <SectionTitle>
            Why Choose <span>Qraft</span>?
          </SectionTitle>
          <SectionSubtitle>
            Powerful features for instant QR code creation and customization
          </SectionSubtitle>
        </SectionHeader>

        <StatsGrid>
          {features.map((feature, index) => (
            <StatCard
              key={index}
              isVisible={isVisible}
              delay={`${index * 100}ms`}
            >
              <LargeBackgroundIcon color={feature.color}>
                {feature.icon}
              </LargeBackgroundIcon>

              <StatLabel>{feature.label}</StatLabel>
              <StatDescription>{feature.description}</StatDescription>
            </StatCard>
          ))}
        </StatsGrid>
      </StatsContainer>
    </StatsSection>
  );
}
